import type { ReactNode } from 'react'
import type { CardReviewSummaryDto } from '@/lib/coaching/card-review-query'
import {
  commentKindLabel,
  commentSourceLabel,
  commentStatusLabel,
  countVisibleComments,
  describeLastReviewed,
  describeSubmission,
  reviewStatusLabel,
} from '../_lib/card-review'
import { HEADING_CLASS } from './section-heading'

/**
 * Where this card's submission stands with review (LG-360 §3).
 *
 * Read-only, deliberately: the Review tab owns writing comments, approving
 * them and moving a review through its lifecycle, and a second place to do
 * any of that would be a second place for those rules to drift. What a coach
 * gets here is the answer to "has this been reviewed, and what did we say" —
 * without leaving the card they are planning.
 *
 * Coach-only, like the history section above it. The client's own view of
 * the same review is `dashboard.feedback.list`, which shows only the
 * approved/posted comments; this shows every comment with its status, since
 * a coach looking at their own queue's work needs to see the drafts and the
 * dismissals too.
 */
export function CardReviewSection(props: {
  /**
   * The review, or null when this card has nothing submitted. Null is only
   * ever rendered as a sentence when the caller knows a review was actually
   * asked for — see `pending`/`error`.
   */
  review: CardReviewSummaryDto | null
  pending: boolean
  /** True when the caller's fetch for this review failed. */
  error?: boolean
}): ReactNode {
  const { review, pending, error = false } = props

  if (pending) {
    // A line, not a skeleton, for the reason `ProblemHistorySection` gives:
    // nothing here has a known height to size one against.
    return (
      <ReviewFrame>
        <p
          aria-busy="true"
          className="text-[11.5px] text-(--cd-text-faint)"
          role="status"
        >
          Loading…
        </p>
      </ReviewFrame>
    )
  }

  if (error) {
    return (
      <ReviewFrame>
        <p
          aria-live="polite"
          className="text-[11.5px] text-(--cd-red)"
          role="status"
        >
          Could not load this card's review.
        </p>
      </ReviewFrame>
    )
  }

  if (review === null) {
    return (
      <ReviewFrame>
        <p className="text-[11.5px] text-(--cd-text-muted)">
          Nothing submitted for review yet.
        </p>
      </ReviewFrame>
    )
  }

  const now = new Date()
  const visible = countVisibleComments(review.comments)
  const lastReviewed = describeLastReviewed(review, now)

  return (
    <ReviewFrame>
      <p className="text-[11.5px] font-semibold text-(--cd-text)">
        {reviewStatusLabel(review.status)}
      </p>
      <p className="text-[10px] text-(--cd-text-faint)">
        {describeSubmission(review, now)}
      </p>
      {lastReviewed !== null ? (
        <p className="text-[10px] text-(--cd-text-faint)">{lastReviewed}</p>
      ) : null}
      {review.comments.length === 0 ? (
        <p className="text-[10.5px] text-(--cd-text-muted)">
          No comments on this review yet.
        </p>
      ) : (
        <>
          {/* Stated because the two numbers are different questions — how
              much work is on the review, and how much of it the client can
              actually see. A count of comments alone reads as the second
              while meaning the first. */}
          <p className="text-[10px] text-(--cd-text-faint)">
            {`${review.comments.length} comment${review.comments.length === 1 ? '' : 's'} · ${visible} visible to the client`}
          </p>
          <ul className="flex flex-col gap-1.5 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-2">
            {review.comments.map((comment) => (
              <li className="flex flex-col gap-0.5" key={comment.id}>
                <span className="text-[9.5px] font-bold tracking-wide text-(--cd-text-faint) uppercase">
                  {`${commentKindLabel(comment.kind)} · ${commentSourceLabel(comment.source)} · ${commentStatusLabel(comment.status)}`}
                </span>
                <span className="text-[10px] text-(--cd-text-faint)">
                  {comment.targetLabel}
                </span>
                {/* `whitespace-pre-line`, matching the Details section: a
                    comment body is written as prose with its own line
                    breaks, and collapsing them runs a coach's paragraphs
                    together. */}
                <span className="text-[10.5px] leading-relaxed whitespace-pre-line text-(--cd-text-muted)">
                  {comment.body}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </ReviewFrame>
  )
}

function ReviewFrame(props: { children: ReactNode }): ReactNode {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className={HEADING_CLASS}>Review</h3>
      {props.children}
    </section>
  )
}
